import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Breadcrumb, Tabs, Button, Dropdown } from "flowbite-react";
import {
  HiOutlineChartBar,
  HiOutlineCheckCircle,
  HiOutlineTrendingUp,
  HiOutlineUsers,
  HiOutlineViewGrid,
  HiAdjustments,
} from "react-icons/hi";

import { useAnalytics } from "../../hooks/useAnalytics";
import { Sidenav } from "../../components";
import { ForecastPageTable } from "./ForecastPageTable";
import { LEGACY_FILTER_KEYS } from "./ForecastPage.utils";
import { RevenueForecastSummary } from "./RevenueForecastSummary";
import {
  ForecastProvider,
  useForecastContext,
} from "../../contexts/ForecastContext";

const SUB_TAB_KEYS = {
  PER_ACCOUNT: "per-account",
  PER_TYPE: "per-type",
};

const ForecastPageContent = () => {
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    trackEvent("forecast_viewed");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [selectedTimeRange, setSelectedTimeRange] = useState(
    "Forecast for next quarter",
  );
  const [activeTab, setActiveTab] = useState("revenue-forecast");
  const [activeSubTab, setActiveSubTab] = useState(SUB_TAB_KEYS.PER_ACCOUNT);
  const hasInitialized = useRef(false);

  // Use ONE context for everything
  const { loading, fetchMultipleTimeRanges, getTimeRangeData } =
    useForecastContext();

  // Define available time ranges directly (no need for a hook)
  const availableTimeRanges = useMemo(() => {
    if (activeTab === "revenue-forecast") {
      return [
        "Forecast for this quarter",
        "Forecast for next quarter",
        "Forecast for next two quarters",
        "Forecast for next 12 months",
      ];
    } else if (activeTab === "open-deals") {
      return [
        "Due this quarter",
        "Due next quarter",
        "Due next 2 quarters",
        "Due in next 12 months",
      ];
    } else if (activeTab === "closed-deals") {
      return [
        "Closed this quarter",
        "Closed last quarter",
        "Closed last 2 quarters",
      ];
    }
    return [];
  }, [activeTab]);

  // Load default time range first, then others progressively
  useEffect(() => {
    if (!hasInitialized.current) {
      const loadInitialData = async () => {
        // Load default time range first
        const defaultTimeRange = "Forecast for next quarter";
        await fetchMultipleTimeRanges([defaultTimeRange]);

        // Then load the rest in the background
        const otherTimeRanges = [
          "Forecast for this quarter",
          "Forecast for next two quarters",
          "Closed this quarter",
          "Due this quarter",
          "Due next quarter",
        ];
        fetchMultipleTimeRanges(otherTimeRanges).then(() => {
          console.log("All background time ranges loaded");
        });
      };

      loadInitialData();
      hasInitialized.current = true;
    }
  }, [fetchMultipleTimeRanges]);

  const handleTabChange = (tabKey) => {
    console.log("Tab changed to:", tabKey);
    setActiveTab(tabKey);
    setActiveSubTab(SUB_TAB_KEYS.PER_ACCOUNT);
    // No need to load data - it's already cached!
  };

  // Get available time ranges based on active tab
  const getAvailableTimeRanges = useCallback(() => {
    return availableTimeRanges;
  }, [availableTimeRanges]);

  // Update time range when switching tabs
  useEffect(() => {
    const filteredRanges = getAvailableTimeRanges();

    if (filteredRanges.length === 0) {
      return;
    }

    if (!filteredRanges.includes(selectedTimeRange)) {
      let defaultRange;
      if (activeTab === "revenue-forecast") {
        defaultRange = "Forecast for next quarter";
      } else if (activeTab === "open-deals") {
        defaultRange =
          filteredRanges.find((range) =>
            range.toLowerCase().includes("due this quarter"),
          ) || filteredRanges[0];
      } else if (activeTab === "closed-deals") {
        defaultRange =
          filteredRanges.find((range) =>
            range.toLowerCase().includes("closed this quarter"),
          ) || filteredRanges[0];
      } else {
        defaultRange = filteredRanges[0];
      }

      console.log("Switching time range to:", defaultRange);
      setSelectedTimeRange(defaultRange);
    }
  }, [activeTab, getAvailableTimeRanges, selectedTimeRange]);

  // Handle time range changes for lazy loading - be more conservative
  useEffect(() => {
    if (activeTab === "revenue-forecast" && selectedTimeRange) {
      const loadTimeRangeData = async () => {
        const currentData = getTimeRangeData(selectedTimeRange);
        if (!currentData) {
          console.log("Fetching data for new time range:", selectedTimeRange);
          await fetchMultipleTimeRanges([selectedTimeRange]);
          console.log("Time range data loaded for:", selectedTimeRange);
        } else {
          console.log("Time range data already cached:", selectedTimeRange);
        }
      };

      const timeoutId = setTimeout(() => {
        loadTimeRangeData();
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [selectedTimeRange, activeTab, getTimeRangeData, fetchMultipleTimeRanges]);

  const handleTimeRangeChange = useCallback(
    (newTimeRange) => {
      const availableRanges = getAvailableTimeRanges();
      if (!availableRanges.includes(newTimeRange)) {
        console.error("Invalid time range selected:", newTimeRange);
        return;
      }
      setSelectedTimeRange(newTimeRange);
    },
    [getAvailableTimeRanges],
  );

  // Get the appropriate legacy filter for data fetching
  const getLegacyFilter = () => {
    switch (activeTab) {
      case "revenue-forecast":
        return LEGACY_FILTER_KEYS.RENEWAL_FORECAST;
      case "open-deals":
        return activeSubTab === SUB_TAB_KEYS.PER_TYPE
          ? LEGACY_FILTER_KEYS.RENEWAL_FORECAST
          : LEGACY_FILTER_KEYS.PIPELINE_EVOLUTION;
      case "closed-deals":
        return activeSubTab === SUB_TAB_KEYS.PER_TYPE
          ? LEGACY_FILTER_KEYS.RENEWAL_FORECAST
          : LEGACY_FILTER_KEYS.RENEWED_ACCOUNTS;
      default:
        return LEGACY_FILTER_KEYS.RENEWAL_FORECAST;
    }
  };

  // Get tab title for breadcrumb and display
  const getTabTitle = () => {
    switch (activeTab) {
      case "revenue-forecast":
        return "Revenue Forecast";
      case "open-deals":
        return "Open Deals";
      case "closed-deals":
        return "Closed Deals";
      default:
        return "Revenue Forecast";
    }
  };

  // Check if current tab needs sub-tabs
  const needsSubTabs = () => {
    return activeTab === "open-deals" || activeTab === "closed-deals";
  };

  // Define the main navigation tabs
  const mainTabs = [
    {
      key: "revenue-forecast",
      label: "Revenue Forecast",
      icon: HiOutlineTrendingUp,
    },
    { key: "open-deals", label: "Open Deals", icon: HiOutlineChartBar },
    { key: "closed-deals", label: "Closed Deals", icon: HiOutlineCheckCircle },
  ];

  // Render time range dropdown
  const renderTimeRangeDropdown = () => (
    <Dropdown
      label={
        <div className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
          <HiAdjustments className="h-4 w-4" />
          <span>{selectedTimeRange}</span>
        </div>
      }
      inline
      disabled={loading}
      className="z-50"
    >
      <Dropdown.Header>
        <span className="text-sm font-medium text-gray-900">
          Select Time Range
        </span>
      </Dropdown.Header>
      {getAvailableTimeRanges().length > 0 ? (
        getAvailableTimeRanges().map((timeRange) => (
          <Dropdown.Item
            key={timeRange}
            onClick={() => handleTimeRangeChange(timeRange)}
            className={`${
              selectedTimeRange === timeRange
                ? "bg-blue-50 text-blue-700"
                : "text-gray-700 hover:bg-gray-50"
            } cursor-pointer`}
          >
            <div className="flex w-full items-center justify-between">
              <span>{timeRange}</span>
              {selectedTimeRange === timeRange && (
                <svg
                  className="h-4 w-4 text-blue-600"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </div>
          </Dropdown.Item>
        ))
      ) : (
        <Dropdown.Item disabled>
          <span className="text-gray-500">No time ranges available</span>
        </Dropdown.Item>
      )}
    </Dropdown>
  );

  // Render content based on current selection
  const renderContent = () => {
    // Show Revenue Forecast Summary for revenue-forecast tab
    if (activeTab === "revenue-forecast") {
      return (
        <RevenueForecastSummary
          selectedTimeRange={selectedTimeRange}
          loading={loading}
        />
      );
    }

    if (!needsSubTabs()) {
      return (
        <div className="mt-4">
          <ForecastPageTable
            selectedFilter={getLegacyFilter()}
            selectedTimeRange={selectedTimeRange}
            showOnlyOpen={activeTab === "open-deals"}
            showOnlyClosed={activeTab === "closed-deals"}
          />
        </div>
      );
    }

    // For Open/Closed Deals, show sub-tabs
    return (
      <div className="w-full">
        <Tabs
          aria-label="View type tabs"
          variant="underline"
          onActiveTabChange={(subTabIndex) => {
            const newSubTab =
              subTabIndex === 0
                ? SUB_TAB_KEYS.PER_ACCOUNT
                : SUB_TAB_KEYS.PER_TYPE;
            setActiveSubTab(newSubTab);
          }}
        >
          <Tabs.Item
            active={activeSubTab === SUB_TAB_KEYS.PER_ACCOUNT}
            title={
              <div className="flex items-center gap-2">
                <HiOutlineUsers className="h-5 w-5" />
                Per Account
              </div>
            }
          >
            <div className="mt-4">
              <ForecastPageTable
                selectedFilter={getLegacyFilter()}
                selectedTimeRange={selectedTimeRange}
                showOnlyOpen={activeTab === "open-deals"}
                showOnlyClosed={activeTab === "closed-deals"}
              />
            </div>
          </Tabs.Item>
          <Tabs.Item
            active={activeSubTab === SUB_TAB_KEYS.PER_TYPE}
            title={
              <div className="flex items-center gap-2">
                <HiOutlineViewGrid className="h-5 w-5" />
                Per Type
              </div>
            }
          >
            <div className="mt-4">
              <ForecastPageTable
                selectedFilter={getLegacyFilter()}
                selectedTimeRange={selectedTimeRange}
                showOnlyOpen={activeTab === "open-deals"}
                showOnlyClosed={activeTab === "closed-deals"}
              />
            </div>
          </Tabs.Item>
        </Tabs>
      </div>
    );
  };

  return (
    <div className="position-relative flex min-h-full flex-1 flex-row">
      <Sidenav />
      <div className="animation-slide-up-lil-bounce flex w-full flex-col items-center overflow-y-scroll pb-12">
        <div className="flex h-full w-[90%] max-w-[1280px] flex-1 flex-col">
          <Breadcrumb className="mt-5">
            <Breadcrumb.Item className="opacity-50">
              {t("pages.forecast.title")}
            </Breadcrumb.Item>
            <Breadcrumb.Item>{getTabTitle()}</Breadcrumb.Item>
            {needsSubTabs() && (
              <Breadcrumb.Item>
                {activeSubTab === "per-type" ? "Per Type" : "Per Account"}
              </Breadcrumb.Item>
            )}
          </Breadcrumb>

          <div className="my-3 flex flex-col items-start justify-start gap-0.5">
            <div className="inline-flex items-center justify-start gap-2">
              <div className="text-4xl font-bold leading-10 text-black">
                {getTabTitle()}
              </div>
            </div>
            <div className="text-sm text-gray-500">
              {selectedTimeRange}
              {needsSubTabs() &&
                ` - ${activeSubTab === "per-type" ? "Per Type" : "Per Account"} View`}
            </div>
          </div>

          {/* Main Navigation Buttons */}
          <div className="my-5 flex w-full flex-row justify-between">
            <div className="flex w-full flex-1 flex-row gap-2.5">
              {mainTabs.map((tab) => {
                const isSelected = activeTab === tab.key;
                const IconComponent = tab.icon;
                return (
                  <Button
                    key={tab.key}
                    color={isSelected ? "blue" : "gray"}
                    onClick={() => handleTabChange(tab.key)}
                    disabled={loading}
                    className={`${
                      isSelected
                        ? "border-blue-600 !bg-blue-600 !text-white"
                        : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                    } transition-colors`}
                  >
                    <IconComponent className="mr-2 h-4 w-4" />
                    {tab.label}
                  </Button>
                );
              })}
            </div>

            <div className="flex items-center gap-2">
              {renderTimeRangeDropdown()}
              {loading && (
                <div className="ml-3 flex items-center text-sm text-gray-500">
                  <svg
                    className="-ml-1 mr-2 h-4 w-4 animate-spin text-gray-500"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Loading...
                </div>
              )}
            </div>
          </div>

          {/* Content Area */}
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export const ForecastPage = () => {
  return (
    <ForecastProvider>
      <ForecastPageContent />
    </ForecastProvider>
  );
};
